/**
 * Theme = 'system' | 'light' | 'dark'.
 *
 * 'system' follows the OS via prefers-color-scheme. light/dark are
 * explicit overrides. Persisted server-side on User.theme + mirrored
 * to localStorage for instant first-paint (so we don't flash light
 * before /api/me responds).
 *
 * Public API:
 *   - applyTheme(theme): set CSS data-theme + persist to localStorage
 *   - readPersistedTheme(): localStorage value or 'system'
 *   - watchSystemPreference(cb): re-applies on OS-theme change when
 *     current pref is 'system'
 */

export type Theme = 'system' | 'light' | 'dark';
const LOCALSTORAGE_KEY = 'lexcrm.theme';

function resolveSystem(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const effective = theme === 'system' ? resolveSystem() : theme;
  document.documentElement.setAttribute('data-theme', effective);
  try {
    localStorage.setItem(LOCALSTORAGE_KEY, theme);
  } catch {
    // private mode etc — skip
  }
}

export function readPersistedTheme(): Theme {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = localStorage.getItem(LOCALSTORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // ignore
  }
  return 'system';
}

/**
 * Subscribe to OS-theme changes. Returns an unsubscribe.
 * The callback fires with the resolved 'light' | 'dark' when the OS
 * preference flips. Caller is responsible for honouring it only when
 * the current persisted theme is 'system'.
 */
export function watchSystemPreference(cb: (resolved: 'light' | 'dark') => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent) => cb(e.matches ? 'dark' : 'light');
  mql.addEventListener('change', handler);
  return () => mql.removeEventListener('change', handler);
}

// Apply the persisted theme synchronously on module load — runs before
// React mounts so there's no flash of light before /api/me returns.
applyTheme(readPersistedTheme());
